import { z } from 'zod';

import type { PetitionEditPageData, PetitionEditPageState } from 'src/app/pages/petitionEditV2/pageContext';

type FormStateDecisionMaker = PetitionEditPageState['formState']['decisionMakers'];
// formState zod schema (array of decision maker objects)
export const decisionMakerSchema = z
	.object({
		id: z.number().optional().or(z.string().optional()),
		displayName: z.string(),
		isConfirmed: z.boolean(),
		title: z.string().nullable(),
		isPerson: z.boolean().nullable(),
		photoUrl: z.string().nullable(),
	})
	.array()
	.optional();

type PetitionDecisionMaker = PetitionEditPageData['petition']['decisionMakersConnection']['nodes'][number];

// transform petition GET API data to formState structure
export const deserializeDecisionMakers = ({
	petition,
}: Pick<PetitionEditPageData, 'petition'>): FormStateDecisionMaker => {
	const getPhotoUrl = (decisionMaker: PetitionDecisionMaker) => {
		if (decisionMaker.photo) return decisionMaker.photo.userMedium.url;
		if (decisionMaker.isPerson) return 'https://static.change.org/profile-img/default-user-profile.svg';
		return 'https://static.change.org/profile-img/default-org-profile.svg';
	};

	const list = petition.decisionMakersConnection.nodes.map((decisionMaker: PetitionDecisionMaker) => {
		return {
			id: parseInt(decisionMaker.id, 10),
			displayName: decisionMaker.displayName,
			isConfirmed: decisionMaker.confirmedAt !== null,
			title: decisionMaker.title ?? decisionMaker.summary,
			isPerson: decisionMaker.isPerson,
			photoUrl: getPhotoUrl(decisionMaker),
		};
	});

	return {
		value: list,
		error: null,
		dirty: false,
	};
};

// transform formState structure to petition PUT API data
export const serializeDecisionMakers = (val: z.infer<typeof decisionMakerSchema>) => {
	if (val) {
		return val.map((dm) => {
			if (dm.isConfirmed) {
				return {
					id: dm.id?.toString(), // graphql expects string | undefined
				};
			}
			return {
				displayName: dm.displayName,
				title: dm.title,
			};
		});
	}
	return [];
};
